import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { call } from '../lib/backbarApi';
import { Empty, ErrorNote, Note, PageHead, Switch } from '../components/ui';

type Game = { slug: string; module_id: string; title: string; is_enabled: boolean; sort_order: number };
type Module = { module_id: string; title: string; sort_order: number };

const REVIEW_BASE =
  ((import.meta.env.VITE_ROOTS_REVIEW_BASE as string | undefined) ?? 'https://review.rootegra.com').replace(/\/+$/, '');

export function Games() {
  const [games, setGames] = useState<Game[] | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [rowError, setRowError] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});

  useEffect(() => {
    (async () => {
      const [g, m] = await Promise.all([
        supabase.from('practice_games').select('slug, module_id, title, is_enabled, sort_order').order('sort_order'),
        supabase.from('curriculum_modules').select('module_id, title, sort_order').order('sort_order'),
      ]);
      if (g.error) {
        setLoadError(g.error);
        return;
      }
      setGames((g.data ?? []) as Game[]);
      setModules((m.data ?? []) as Module[]);
    })();
  }, []);

  const groups = useMemo(() => {
    if (!games) return [];
    const byModule = new Map<string, Game[]>();
    for (const g of games) {
      const list = byModule.get(g.module_id) ?? [];
      list.push(g);
      byModule.set(g.module_id, list);
    }
    const order = new Map(modules.map((m, i) => [m.module_id, i]));
    const title = new Map(modules.map((m) => [m.module_id, m.title]));
    return [...byModule.entries()]
      .sort((a, b) => (order.get(a[0]) ?? 999) - (order.get(b[0]) ?? 999))
      .map(([moduleId, list]) => ({
        moduleId,
        title: title.get(moduleId) ?? moduleId,
        games: list.sort((a, b) => a.sort_order - b.sort_order),
      }));
  }, [games, modules]);

  async function toggle(game: Game, enabled: boolean) {
    // Optimistic: flip now, put it back if the server says no.
    setGames((gs) => gs?.map((g) => (g.slug === game.slug ? { ...g, is_enabled: enabled } : g)) ?? gs);
    setBusy((b) => ({ ...b, [game.slug]: true }));
    setRowError((e) => ({ ...e, [game.slug]: null }));
    try {
      await call('games.set_enabled', { slug: game.slug, enabled });
    } catch (err) {
      setGames((gs) => gs?.map((g) => (g.slug === game.slug ? { ...g, is_enabled: !enabled } : g)) ?? gs);
      setRowError((e) => ({ ...e, [game.slug]: err }));
    } finally {
      setBusy((b) => ({ ...b, [game.slug]: false }));
    }
  }

  const onCount = games?.filter((g) => g.is_enabled).length ?? 0;

  return (
    <>
      <PageHead
        title="Games"
        sub={
          <>
            Off hides the game from every apprentice within a minute. Nothing is deleted.
            {games && ` ${onCount} of ${games.length} games are on.`}
          </>
        }
      />

      <ErrorNote error={loadError} />
      {games && games.length === 0 && <Empty>No games are listed yet.</Empty>}
      {!games && !loadError && <Empty>Loading…</Empty>}

      {groups.map((grp) => (
        <section key={grp.moduleId} className="lp-panel" style={{ marginTop: 14 }}>
          <h2 className="lp-section-title" style={{ fontSize: 24, marginBottom: 6 }}>{grp.title}</h2>
          <ul className="lp-list">
            {grp.games.map((g) => (
              <li key={g.slug} className="lp-item">
                <div className="lp-item-main">
                  <div className="lp-item-title">{g.title}</div>
                  <div className="lp-item-line">
                    <a
                      href={`${REVIEW_BASE}/games/${g.slug}/${g.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--moss-deep)', textDecoration: 'underline', textUnderlineOffset: 3 }}
                    >
                      Try it
                    </a>
                    {' · opens in a new tab'}
                  </div>
                  {rowError[g.slug] ? (
                    <Note tone="warn">The switch did not save. Try again in a moment.</Note>
                  ) : null}
                </div>
                <Switch
                  on={g.is_enabled}
                  disabled={Boolean(busy[g.slug])}
                  label={`${g.title} is ${g.is_enabled ? 'on' : 'off'}`}
                  onChange={(v) => void toggle(g, v)}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}
